// src/context/NotificationsContext.tsx
//
// Ported from the old project's NotificationsContext. Real functionality
// kept: fetching the user's notifications, listening for live Firebase push
// messages, and refetching when one arrives. Changed to fit this project —
// see prior history for the @/lib/hook -> @/services/hook etc. renames.
//
// Real-ified again for the NEW notification shape (confirmed from a real
// payload, replacing the old email-style {subject,to,from,message} one that
// had no id):
//   { id, type, category, icon, actorUsername, actorName, actorAvatarUrl,
//     body, entityType, entityId, amount, currency, read, createdAt }
// This one HAS an id and a `read` flag, which the old shape never did.
//
// markAsRead/markAllAsRead/unreadCount are now real — PATCH
// notifications/{id}/read, POST notifications/read-all, GET
// notifications/unread-count. These weren't in any service's swagger docs
// as of this writing (checked all five), but an unauthenticated probe of
// each returned 500, not 404 — the routes exist and are handled, the 500s
// are just an auth-less request falling over inside the handler, not a
// missing mapping. Given that, and that these were asked for directly
// rather than found independently, each mutation still keeps the same
// optimistic-update-with-rollback shape the rest of this app uses for a
// freshly-wired endpoint, so a wrong assumption about the response shape
// degrades to "reverts on error" rather than a silent lie about read state.
//
// NEEDS CONFIRMATION before trusting this fully:
//   1. The list endpoint URL itself (`notifications?email=...`) is
//      unconfirmed to have changed alongside the response shape — kept
//      as-is since nothing said otherwise. Page envelope handled
//      defensively (flat array per page, or a {content:[...]} wrapper)
//      since which one this new system uses isn't confirmed either.
//   2. `onMessageListener` — imported from "@/services/firebase" below. The
//      old project had this in "@/oauth/firebaseConfig". If this project's
//      firebase.ts doesn't export it yet, it needs adding there (same shape:
//      returns a Promise that resolves once per incoming message).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetData, useInfiniteGetData } from "@/hooks/api/use-api";
import { useAppSelector } from "@/services/hook";
import type { RootState } from "@/services/store";
import { useAppStore } from "@/lib/core";
import { onMessageListener } from "@/services/firebase";
import api from "@/services/axios";

/** Real shape, confirmed from an actual payload. Only "MONETIZATION" and
 *  "NEW_SUBSCRIBER" are confirmed values for category/type — both are kept
 *  as open string types rather than narrow unions since more almost
 *  certainly exist. */
export interface NotificationItem {
  id: number;
  type: string;
  category: string;
  icon: string;
  actorUsername: string | null;
  actorName: string | null;
  actorAvatarUrl: string | null;
  body: string;
  entityType: string | null;
  entityId: string | null;
  amount: number | null;
  currency: string | null;
  read: boolean;
  createdAt: string;
}

interface FCMPayload {
  title?: string;
  body?: string;
  icon?: string;
  image?: string;
}

interface NotificationsContextType {
  notifications: NotificationItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetchNotifications: () => void;
  markAsRead: (id: number) => void;
  markAllAsRead: () => void;
  isMarkingAllAsRead: boolean;
  /** -1 until the first real fetch resolves, so callers can tell "unknown
   *  yet" apart from "confirmed zero" if that distinction ever matters. */
  unreadCount: number;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { userObject } = useAppSelector((s: RootState) => s.auth);
  const toast = useAppStore((s) => s.toast);
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteGetData({
    url: `notifications?email=${userObject?.email}&sort=createdAt,desc`,
    queryKey: ["GetNotificationsByEmail", userObject?.email],
    pageSize: 20,
    enabled: !!userObject?.email,
  });

  const notifications: NotificationItem[] = useMemo(
    () =>
      data?.pages?.flatMap(
        (page: any) => (Array.isArray(page) ? page : (page?.content ?? [])) ?? [],
      ) ?? [],
    [data],
  );

  const { data: unreadCountData } = useGetData({
    url: "notifications/unread-count",
    queryKey: ["GetUnreadNotificationsCount", userObject?.email],
    enabled: !!userObject?.email,
  });
  // Response shape isn't confirmed (not in any swagger doc — see file
  // header), so this reads whichever of a bare number or a {count:} /
  // {unreadCount:} wrapper actually comes back rather than assuming one.
  const unreadCount = useMemo(() => {
    const raw = (unreadCountData as { data?: unknown } | undefined)?.data;
    if (typeof raw === "number") return raw;
    if (raw && typeof raw === "object") {
      const obj = raw as Record<string, unknown>;
      const val = obj.count ?? obj.unreadCount ?? obj.total;
      if (typeof val === "number") return val;
    }
    return -1;
  }, [unreadCountData]);

  const invalidateUnreadCount = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["GetUnreadNotificationsCount"],
        exact: false,
      }),
    [queryClient],
  );

  // Optimistic read-tracking, now backed by a real PATCH per id. A plain
  // api.patch call rather than useCustomMutation: markAsRead takes an id
  // at CALL time (any row, any time) rather than through a bound
  // component's own props, so there's no single stable endpoint string a
  // mutation hook's closure could capture — same reason app-store.ts's
  // block/unblock call the axios instance directly instead of going
  // through a hook.
  const [locallyRead, setLocallyRead] = useState<Set<number>>(new Set());
  const markAsRead = useCallback(
    (id: number) => {
      if (notifications.find((n) => n.id === id)?.read) return;
      setLocallyRead((prev) => new Set(prev).add(id));
      api
        .patch(`notifications/${id}/read`)
        .then(invalidateUnreadCount)
        .catch(() => {
          setLocallyRead((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          toast("Couldn't mark as read — try again", "err");
        });
    },
    [notifications, toast, invalidateUnreadCount],
  );

  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const markAllAsRead = useCallback(() => {
    const previouslyRead = new Set(locallyRead);
    setIsMarkingAllAsRead(true);
    setLocallyRead(new Set(notifications.map((n) => n.id)));
    api
      .post("notifications/read-all")
      .then(() => {
        toast("All notifications marked as read", "ok");
        invalidateUnreadCount();
      })
      .catch(() => {
        setLocallyRead(previouslyRead);
        toast("Couldn't mark all as read — try again", "err");
      })
      .finally(() => setIsMarkingAllAsRead(false));
  }, [notifications, locallyRead, toast, invalidateUnreadCount]);

  const notificationsWithLocalRead = useMemo(
    () =>
      notifications.map((n) =>
        locallyRead.has(n.id) && !n.read ? { ...n, read: true } : n,
      ),
    [notifications, locallyRead],
  );

  const handleIncomingMessage = useCallback(
    (payload: { notification?: FCMPayload; data?: FCMPayload }) => {
      const notification = payload.notification || payload.data;
      if (!notification) return;

      toast(notification.body || "You have a new notification", "ok");

      if (
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        new Notification(notification.title || "New notification", {
          body: notification.body,
          icon: notification.icon || notification.image || "/logo.png",
        });
      }

      refetch();
    },
    [toast, refetch],
  );

  // Recursive listener, carried over as-is from the old project: each call
  // to onMessageListener() resolves once, so the next one is queued
  // immediately after, and again after an error (with a short backoff).
  useEffect(() => {
    let isListening = true;

    const setupListener = () => {
      if (!isListening) return;
      onMessageListener()
        .then((payload: any) => {
          handleIncomingMessage(payload);
          setupListener();
        })
        .catch((err: unknown) => {
          console.error("Error receiving FCM notification:", err);
          if (isListening) setTimeout(setupListener, 1000);
        });
    };

    setupListener();
    return () => {
      isListening = false;
    };
  }, [handleIncomingMessage]);

  useEffect(() => {
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission();
    }
  }, []);

  return (
    <NotificationsContext.Provider
      value={{
        notifications: notificationsWithLocalRead,
        isLoading,
        hasNextPage: !!hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        refetchNotifications: refetch,
        markAsRead,
        markAllAsRead,
        isMarkingAllAsRead,
        unreadCount,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationsProvider",
    );
  }
  return context;
};
